import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { AgencyFilterHeader } from './AgencyFilterHeader';
import { AgencyFilterResults } from './AgencyFilterResults';
import scrollTop from '../../../utils/helpers/smoothScroll';
import { AgencyFilterContainer } from './styled';

interface AgencyFilterProps {
  agencyId?: string;
  categories?: {
    __typename: 'Category';
    id: string;
    pluralName: string;
  }[];
  allCategories?: {
    __typename: 'Category';
    id: string;
    title: string;
    children: Array<{
      __typename: 'Category';
      id: string;
      title: string;
    }>;
  }[];
  isCurrentItemPage?: boolean;
}

export const AgencyFilter: React.FC<AgencyFilterProps> = ({
  categories = [],
  allCategories = [],
  agencyId,
  isCurrentItemPage = false
}) => {
  const { t } = useTranslation();
  const { isWebView } = useBridgeState();
  const wrapperRef = useRef<HTMLElement | null>(null);
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState({
    id: 'all'
  });

  const [activeLeasedCategory, setActiveLeasedCategory] = useState([
    'lease',
    'sale'
  ]);

  const combinedCategories = [
    { id: 'lease', pluralName: t('pages.agency.lease') },
    { id: 'sale', pluralName: t('pages.agency.sale') },
    ...categories
  ].map(categ => {
    const found = allCategories
      .flatMap(item => (item.children.length ? item.children : [item]))
      .find(child => child.id === categ.id);

    return {
      ...categ,
      title: found ? found.title : null
    };
  });

  const bottomOffset = isWebView ? 50 : 20;

  const onClickFilterItem = (id: string, isSetLeasedCategories = true) => {
    const chipEl = document.getElementById(`chip-${id}`);

    if (chipEl && categoriesContainerRef.current) {
      const container = categoriesContainerRef.current;

      const chipLeft = chipEl.offsetLeft;
      const chipWidth = chipEl.offsetWidth;
      const containerWidth = container.offsetWidth;

      const scrollLeft = chipLeft - containerWidth / 2 + chipWidth / 2;

      container.scrollLeft = scrollLeft;
    }

    if (wrapperRef.current) {
      const offsetTop =
        wrapperRef.current.offsetTop - (isCurrentItemPage ? bottomOffset : 0);

      scrollTop(
        document.documentElement || document.scrollingElement || document.body,
        offsetTop
      );
    }
    setCategory({ id });
    if (isSetLeasedCategories) setActiveLeasedCategory(['lease', 'sale']);
  };

  return (
    <AgencyFilterContainer data-cy="agency-filter" ref={wrapperRef}>
      <AgencyFilterHeader
        categories={combinedCategories}
        activeCategoryId={category.id.toString()}
        onClickItem={onClickFilterItem}
        categoriesContainerRef={categoriesContainerRef}
      />
      <AgencyFilterResults
        agencyId={agencyId}
        categories={combinedCategories}
        activeCategoryId={category.id.toString()}
        onClickItem={onClickFilterItem}
        activeLeasedCategory={activeLeasedCategory}
        setActiveLeasedCategory={setActiveLeasedCategory}
      />
    </AgencyFilterContainer>
  );
};
